import React from 'react';
import type { FileResponseItem } from '../models.ts';
import { ImageWrapper } from '@components/ImageWrapper.tsx';
import { ImgFileZip } from '@components/Images/imgFileZip.tsx';
import { ImgFileGeneric } from '@components/Images/imgFileGeneric.tsx';
import { ImgFilePdf } from '@components/Images/ImgFilePdf.tsx';
import { ImgFileDoc } from '@components/Images/ImgFileDoc.tsx';
import { ImgFileDocx } from '@components/Images/ImgFileDocx.tsx';
import { ImgFileTxt } from '@components/Images/ImgFileTxt.tsx';
import { ImgFileCsv } from '@components/Images/ImgFileCsv.tsx';
import { ImgFileHtml } from '@components/Images/ImgFileHtml.tsx';
import { ImgFileSvg } from '@components/Images/ImgFileSvg.tsx';
import { ImgFileSql } from '@components/Images/ImgFileSql.tsx';
import { ImgFileLog } from '@components/Images/ImgFIleLog.tsx';

type FileIconProps = {
  fileName: string;
  /** Provide fileId to enable server-hosted image and zip previews. Omit for in-progress uploads. */
  fileId?: string;
  /** Full file metadata (used for zip image preview thumbnails). */
  file?: FileResponseItem;
  /** Controls compact rendering of zip folder preview in list view. */
  view?: 'grid' | 'list' | 'default';
};

function ZipFolderPreview({ fileId, entries, compact, totalFiles }: { fileId: string; entries: string[]; compact: boolean; totalFiles?: number }) {
  return (
    <div className={`zip-folder-visual${compact ? ' compact' : ''}`}>
      <div className="folder-container zip-folder-container">
        <div className="folder-tab" />
        <div className="folder-back">
          <div className="image-count">{totalFiles ?? entries.length}</div>
          <div className="folder-preview">
            {entries.slice(0, 4).map((entry, i) => (
              <img
                key={i}
                src={`/api/file/${fileId}/entry/${encodeURI(entry)}/preview?width=120&height=120`}
                alt={entry}
                className="preview-image"
                loading="lazy"
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export const FileIcon: React.FC<FileIconProps> = ({ fileName, fileId, file, view = 'default' }) => {
  const extension = fileName.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'webp':
    case 'bmp':
      if (fileId) {
        return (
          <ImageWrapper
            src={fileId}
            alt={fileName}
            onPreviewSrc={(id) => `/api/file/${id}/preview?width=120&height=120`}
            onViewSrc={(id) => `/api/file/${id}/preview?width=1536&height=1536`}
            onDownloadSrc={(id) => `/api/file/${id}/download`}
            onAltText={(id) => `Image preview for ${id}`}
          />
        );
      }
      return <ImgFileGeneric size={64} ext={extension} />;
    case 'zip':
    case '7z':
    case 'rar':
      if (fileId && file?.zipImagePreviews?.length) {
        return <ZipFolderPreview fileId={fileId} entries={file.zipImagePreviews} totalFiles={file.zipTotalFiles} compact={view === 'list'} />;
      }
      return <ImgFileZip size={64} />;
    case 'pdf':
      return <ImgFilePdf size={64} />;
    case 'doc':
      return <ImgFileDoc size={64} />;
    case 'docx':
      return <ImgFileDocx size={64} />;
    case 'txt':
      return <ImgFileTxt size={64} />;
    case 'csv':
      return <ImgFileCsv size={64} />;
    case 'html':
    case 'htm':
      return <ImgFileHtml size={64} />;
    case 'svg':
      return <ImgFileSvg size={64} />;
    case 'sql':
      return <ImgFileSql size={64} />;
    case 'log':
      return <ImgFileLog size={64} />;
    default:
      return <ImgFileGeneric size={64} ext={extension} />;
  }
};
